import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@core/auth/useAuth'
import { ApiError } from '@core/api/client'
import {
  adminApi,
  type AddMemberResult,
  type AuditAggregate,
  type AuditEntry,
  type Member,
  type ModuleCatalogEntry,
  type Organization,
  type Workspace,
  type WorkspaceModule,
} from '@core/api/admin'

function fmt(ts: string | null): string {
  if (!ts) return '-'
  try {
    return new Date(ts).toISOString().slice(0, 16).replace('T', ' ')
  } catch {
    return ts
  }
}

function errMsg(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: string } | null
    return body?.detail ?? `HTTP ${err.status}`
  }
  return 'Erro inesperado'
}

// ---------- Organizations column ----------

function OrgColumn({
  selected,
  onSelect,
}: {
  selected: string | null
  onSelect: (id: string | null) => void
}) {
  const [orgs, setOrgs] = useState<Organization[] | null>(null)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [error, setError] = useState('')

  const reload = useCallback(async () => {
    setOrgs(await adminApi.listOrganizations())
  }, [])

  useEffect(() => {
    // A regra não vê para lá do `await`: o `reload` só escreve
    // estado depois de `await adminApi.listOrganizations()`, por isso
    // não há render em cascata para evitar aqui.
     
    // O `reload` só escreve estado DEPOIS do await, portanto não há
    // render encadeado aqui. A regra é conservadora: marca qualquer
    // chamada a uma função que contenha setState, sem distinguir o
    // que é síncrono do que não é. Verificado em cada chamada.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void reload()
  }, [reload])

  const onCreate = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    try {
      const created = await adminApi.createOrganization(name.trim(), slug.trim())
      setName('')
      setSlug('')
      setCreating(false)
      await reload()
      onSelect(created.id)
    } catch (err) {
      setError(errMsg(err))
    }
  }

  return (
    <aside className="flex w-64 shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-panel)]">
      <header className="flex items-center justify-between border-b border-[var(--color-border-subtle)] px-3 py-2 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
        <span>Organizações</span>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="rounded border border-[var(--color-border)] px-1.5 text-[10px] normal-case hover:text-[var(--color-text)]"
        >
          {creating ? 'Cancelar' : 'Nova'}
        </button>
      </header>
      {creating && (
        <form
          onSubmit={onCreate}
          className="flex flex-col gap-2 border-b border-[var(--color-border-subtle)] p-3 text-xs"
        >
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome"
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1"
          />
          <input
            required
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="slug"
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1"
          />
          {error && <span className="text-red-400">{error}</span>}
          <button
            type="submit"
            className="rounded bg-[var(--color-accent)] px-2 py-1 font-medium text-[var(--color-bg)]"
          >
            Criar
          </button>
        </form>
      )}
      <ul className="flex-1 overflow-y-auto">
        {orgs === null ? (
          <li className="p-3 text-xs text-[var(--color-text-dim)]">A carregar…</li>
        ) : orgs.length === 0 ? (
          <li className="p-3 text-xs text-[var(--color-text-dim)]">
            Sem organizações.
          </li>
        ) : (
          orgs.map((o) => {
            const isActive = o.id === selected
            return (
              <li
                key={o.id}
                className={`border-b border-[var(--color-border-subtle)] ${
                  isActive
                    ? 'border-l-2 border-l-[var(--color-accent)] bg-[var(--color-panel-elevated)]'
                    : 'hover:bg-[var(--color-panel-elevated)]'
                }`}
              >
                <button
                  type="button"
                  onClick={() => onSelect(o.id)}
                  className="w-full px-3 py-2 text-left"
                >
                  <div className="text-sm font-medium">{o.name}</div>
                  <div className="text-xs text-[var(--color-text-dim)]">
                    {o.slug}
                  </div>
                </button>
              </li>
            )
          })
        )}
      </ul>
    </aside>
  )
}

// ---------- Workspaces column ----------

function WorkspaceColumn({
  organizationId,
  selected,
  onSelect,
}: {
  organizationId: string | null
  selected: string | null
  onSelect: (id: string | null) => void
}) {
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null)
  const [creating, setCreating] = useState(false)
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [error, setError] = useState('')

  const reload = useCallback(async () => {
    if (!organizationId) {
      setWorkspaces(null)
      return
    }
    setWorkspaces(await adminApi.listWorkspaces(organizationId))
  }, [organizationId])

  useEffect(() => {
    // A regra não vê para lá do `await`: o `reload` só escreve
    // estado depois de `await adminApi.listWorkspaces()`, por isso não
    // há render em cascata para evitar aqui.
     
    // O `reload` só escreve estado DEPOIS do await, portanto não há
    // render encadeado aqui. A regra é conservadora: marca qualquer
    // chamada a uma função que contenha setState, sem distinguir o
    // que é síncrono do que não é. Verificado em cada chamada.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void reload()
  }, [reload])

  if (!organizationId) {
    return (
      <aside className="flex w-72 shrink-0 items-center justify-center border-r border-[var(--color-border)] bg-[var(--color-panel)] p-4 text-xs text-[var(--color-text-dim)]">
        Seleciona uma organização.
      </aside>
    )
  }

  const onCreate = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    try {
      const created = await adminApi.createWorkspace(
        organizationId,
        slug.trim(),
        name.trim(),
        expiresAt ? new Date(expiresAt).toISOString() : null,
      )
      setSlug('')
      setName('')
      setExpiresAt('')
      setCreating(false)
      await reload()
      onSelect(created.id)
    } catch (err) {
      setError(errMsg(err))
    }
  }

  return (
    <aside className="flex w-72 shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-panel)]">
      <header className="flex items-center justify-between border-b border-[var(--color-border-subtle)] px-3 py-2 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
        <span>Workspaces</span>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="rounded border border-[var(--color-border)] px-1.5 text-[10px] normal-case hover:text-[var(--color-text)]"
        >
          {creating ? 'Cancelar' : 'Novo'}
        </button>
      </header>
      {creating && (
        <form
          onSubmit={onCreate}
          className="flex flex-col gap-2 border-b border-[var(--color-border-subtle)] p-3 text-xs"
        >
          <input
            required
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="slug"
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1"
          />
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome"
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1"
          />
          <label className="flex flex-col gap-0.5">
            <span className="text-[var(--color-text-dim)]">Expira (opcional)</span>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1"
            />
          </label>
          {error && <span className="text-red-400">{error}</span>}
          <button
            type="submit"
            className="rounded bg-[var(--color-accent)] px-2 py-1 font-medium text-[var(--color-bg)]"
          >
            Criar
          </button>
        </form>
      )}
      <ul className="flex-1 overflow-y-auto">
        {workspaces === null ? (
          <li className="p-3 text-xs text-[var(--color-text-dim)]">A carregar…</li>
        ) : workspaces.length === 0 ? (
          <li className="p-3 text-xs text-[var(--color-text-dim)]">
            Sem workspaces.
          </li>
        ) : (
          workspaces.map((w) => {
            const isActive = w.id === selected
            const isArchived = w.archived_at !== null
            return (
              <li
                key={w.id}
                className={`border-b border-[var(--color-border-subtle)] ${
                  isActive
                    ? 'border-l-2 border-l-[var(--color-accent)] bg-[var(--color-panel-elevated)]'
                    : 'hover:bg-[var(--color-panel-elevated)]'
                }`}
              >
                <button
                  type="button"
                  onClick={() => onSelect(w.id)}
                  className="w-full px-3 py-2 text-left"
                >
                  <div
                    className={`text-sm font-medium ${
                      isArchived
                        ? 'text-[var(--color-text-dim)] line-through decoration-[var(--color-text-dim)]/50'
                        : ''
                    }`}
                  >
                    {w.name}
                  </div>
                  <div className="text-xs text-[var(--color-text-dim)]">
                    {w.slug}
                    {w.expires_at && ` · expira ${fmt(w.expires_at)}`}
                    {isArchived && ' · arquivado'}
                  </div>
                </button>
              </li>
            )
          })
        )}
      </ul>
    </aside>
  )
}

// ---------- Members section ----------

function MembersSection({
  workspaceId,
  modulesVersion,
}: {
  workspaceId: string
  modulesVersion: number
}) {
  const [members, setMembers] = useState<Member[] | null>(null)
  const [workspaceModules, setWorkspaceModules] = useState<WorkspaceModule[]>(
    [],
  )
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [sendInvite, setSendInvite] = useState(true)
  const [error, setError] = useState('')
  const [lastInvite, setLastInvite] = useState<AddMemberResult | null>(null)
  const [pwUserId, setPwUserId] = useState<string | null>(null)
  const [pwValue, setPwValue] = useState('')
  const [pwBusy, setPwBusy] = useState(false)
  const [pwOkFor, setPwOkFor] = useState<string | null>(null)
  const [grantBusyFor, setGrantBusyFor] = useState<string | null>(null)

  const reload = useCallback(async () => {
    const [m, wm] = await Promise.all([
      adminApi.listMembers(workspaceId),
      adminApi.listWorkspaceModules(workspaceId),
    ])
    setMembers(m)
    setWorkspaceModules(wm)
  }, [workspaceId])

  // O convite mostrado pertence ao workspace anterior: limpa-se ao
  // trocar, durante o render e com guarda. Limpar no corpo do efeito
  // deixava um render com o convite antigo sob o cabeçalho novo.
  const [wsAdoptado, setWsAdoptado] = useState(workspaceId)
  if (workspaceId !== wsAdoptado) {
    setWsAdoptado(workspaceId)
    setLastInvite(null)
  }

  useEffect(() => {
    // O `reload` só escreve estado DEPOIS do await, portanto não há
    // render encadeado aqui. A regra é conservadora: marca qualquer
    // chamada a uma função que contenha setState, sem distinguir o
    // que é síncrono do que não é. Verificado em cada chamada.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void reload()
    // Re-runs when modulesVersion ticks (parent's ModulesSection
    // toggled a module on/off) so the per-member buttons reflect the
    // updated workspace_modules set without a manual refresh.
  }, [reload, modulesVersion])

  const onToggleModule = async (
    userId: string,
    slug: string,
    isOn: boolean,
  ) => {
    const member = members?.find((m) => m.user_id === userId)
    if (!member) return
    const next = isOn
      ? member.module_slugs.filter((x) => x !== slug)
      : [...member.module_slugs, slug]
    setGrantBusyFor(userId)
    setError('')
    try {
      const updated = await adminApi.setMemberModules(
        workspaceId,
        userId,
        next,
      )
      setMembers((prev) =>
        (prev ?? []).map((m) => (m.user_id === userId ? updated : m)),
      )
    } catch (err) {
      setError(errMsg(err))
    } finally {
      setGrantBusyFor(null)
    }
  }

  const onAdd = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLastInvite(null)
    try {
      const result = await adminApi.addMember(
        workspaceId,
        email.trim(),
        name.trim() || null,
        sendInvite,
      )
      setEmail('')
      setName('')
      setLastInvite(result)
      await reload()
    } catch (err) {
      setError(errMsg(err))
    }
  }

  const onRevoke = async (userId: string, memberEmail: string) => {
    if (!window.confirm(`Revogar acesso de ${memberEmail}?`)) return
    try {
      await adminApi.revokeMember(workspaceId, userId)
      await reload()
    } catch (err) {
      setError(errMsg(err))
    }
  }

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault()
    if (!pwUserId || pwValue.length < 8) {
      setError('Password tem de ter pelo menos 8 caracteres.')
      return
    }
    setPwBusy(true)
    setError('')
    try {
      await adminApi.setUserPassword(pwUserId, pwValue)
      setPwOkFor(pwUserId)
      setPwValue('')
      setPwUserId(null)
    } catch (err) {
      setError(errMsg(err))
    } finally {
      setPwBusy(false)
    }
  }

  return (
    <section className="rounded border border-[var(--color-border)] bg-[var(--color-panel)]">
      <header className="border-b border-[var(--color-border-subtle)] px-3 py-2 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
        Membros
      </header>
      <p className="border-b border-[var(--color-border-subtle)] px-3 py-2 text-[11px] text-[var(--color-text-dim)]">
        Cada membro tem sempre o viewer base. Os módulos opcionais
        (BCF, etc.) são concedidos individualmente na coluna
        &ldquo;Acessos&rdquo;: só aparecem pills para módulos
        ativados na secção mais abaixo deste workspace.
      </p>
      <form onSubmit={onAdd} className="flex flex-wrap items-end gap-2 p-3 text-xs">
        <label className="flex flex-1 min-w-[200px] flex-col gap-1">
          <span className="text-[var(--color-text-dim)]">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1"
          />
        </label>
        <label className="flex flex-1 min-w-[160px] flex-col gap-1">
          <span className="text-[var(--color-text-dim)]">Nome (opcional)</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1"
          />
        </label>
        <label className="flex items-center gap-1.5 self-end pb-1.5">
          <input
            type="checkbox"
            checked={sendInvite}
            onChange={(e) => setSendInvite(e.target.checked)}
          />
          <span>Enviar magic link</span>
        </label>
        <button
          type="submit"
          className="rounded bg-[var(--color-accent)] px-3 py-1.5 font-medium text-[var(--color-bg)]"
        >
          Adicionar
        </button>
      </form>
      {error && <div className="px-3 pb-2 text-xs text-red-400">{error}</div>}
      {pwUserId && (
        <form
          onSubmit={submitPassword}
          className="flex flex-wrap items-end gap-2 border-t border-[var(--color-border-subtle)] bg-[var(--color-panel-elevated)] px-3 py-2 text-xs"
        >
          <span className="text-[var(--color-text-muted)]">
            Definir password para{' '}
            <span className="text-[var(--color-text)]">
              {members?.find((m) => m.user_id === pwUserId)?.email ?? 'utilizador'}
            </span>
          </span>
          <input
            type="text"
            value={pwValue}
            onChange={(e) => setPwValue(e.target.value)}
            placeholder="Mínimo 8 caracteres"
            autoFocus
            className="flex-1 min-w-[180px] rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 font-mono"
          />
          <button
            type="submit"
            disabled={pwBusy || pwValue.length < 8}
            className="rounded bg-[var(--color-accent)] px-3 py-1 font-medium text-[var(--color-bg)] disabled:opacity-50"
          >
            {pwBusy ? 'A guardar…' : 'Guardar'}
          </button>
          <button
            type="button"
            onClick={() => {
              setPwUserId(null)
              setPwValue('')
            }}
            className="rounded border border-[var(--color-border)] px-3 py-1 text-[var(--color-text-muted)]"
          >
            Cancelar
          </button>
          <span className="text-[var(--color-text-dim)]">
            Comunica a password ao utilizador por canal seguro.
          </span>
        </form>
      )}
      {lastInvite?.invite_url && (
        <div className="border-t border-[var(--color-border-subtle)] bg-[var(--color-accent-bg)] px-3 py-2 text-xs">
          <div className="text-[var(--color-accent)]">
            Convite enviado a {lastInvite.member.email}.
          </div>
          <div className="mt-1 truncate text-[var(--color-text-dim)]" title={lastInvite.invite_url}>
            URL: {lastInvite.invite_url}
          </div>
        </div>
      )}
      <table className="w-full text-xs">
        <thead className="text-[var(--color-text-dim)]">
          <tr>
            <th className="border-y border-[var(--color-border-subtle)] px-3 py-1.5 text-left font-normal">Email</th>
            <th className="border-y border-[var(--color-border-subtle)] px-3 py-1.5 text-left font-normal">Role</th>
            <th className="border-y border-[var(--color-border-subtle)] px-3 py-1.5 text-left font-normal">Acessos</th>
            <th className="border-y border-[var(--color-border-subtle)] px-3 py-1.5 text-left font-normal">Estado</th>
            <th className="border-y border-[var(--color-border-subtle)] px-3 py-1.5"></th>
          </tr>
        </thead>
        <tbody>
          {members === null ? (
            <tr>
              <td colSpan={5} className="p-3 text-[var(--color-text-dim)]">A carregar…</td>
            </tr>
          ) : members.length === 0 ? (
            <tr>
              <td colSpan={5} className="p-3 text-[var(--color-text-dim)]">Sem membros.</td>
            </tr>
          ) : (
            members.map((m) => (
              <tr key={m.user_id} className="border-b border-[var(--color-border-subtle)]">
                <td className="px-3 py-1.5">
                  <div>{m.email}</div>
                  {m.name && (
                    <div className="text-[var(--color-text-dim)]">{m.name}</div>
                  )}
                </td>
                <td className="px-3 py-1.5">{m.role}</td>
                <td className="px-3 py-1.5">
                  <div className="flex flex-wrap items-center gap-1">
                    <span
                      className="rounded border border-[var(--color-border)] bg-[var(--color-panel-elevated)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)]"
                      title="Toda a gente tem acesso ao viewer base."
                    >
                      Viewer
                    </span>
                    {workspaceModules.length === 0 && (
                      <span className="text-[10px] text-[var(--color-text-dim)]">
                        sem módulos no workspace
                      </span>
                    )}
                    {workspaceModules.map((wm) => {
                      const granted = m.module_slugs.includes(wm.slug)
                      return (
                        <button
                          key={wm.slug}
                          type="button"
                          disabled={
                            grantBusyFor === m.user_id || !!m.revoked_at
                          }
                          onClick={() =>
                            void onToggleModule(m.user_id, wm.slug, granted)
                          }
                          className={`rounded border px-1.5 py-0.5 text-[10px] disabled:opacity-50 ${
                            granted
                              ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                              : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                          }`}
                          title={
                            granted
                              ? `Remover acesso a ${wm.name}`
                              : `Conceder acesso a ${wm.name}`
                          }
                        >
                          {wm.name}
                        </button>
                      )
                    })}
                  </div>
                </td>
                <td className="px-3 py-1.5">
                  {m.revoked_at ? (
                    <span className="text-[var(--color-text-dim)]">
                      Revogado {fmt(m.revoked_at)}
                    </span>
                  ) : (
                    <span className="text-[var(--color-accent)]">Ativo</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right">
                  <div className="flex justify-end gap-1">
                    {!m.revoked_at && (
                      <button
                        type="button"
                        onClick={() => {
                          setPwUserId(m.user_id)
                          setPwValue('')
                          setPwOkFor(null)
                        }}
                        className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                        title="Definir ou repor a password deste utilizador"
                      >
                        Definir password
                      </button>
                    )}
                    {pwOkFor === m.user_id && (
                      <span className="text-[var(--color-accent)]">✓</span>
                    )}
                    {!m.revoked_at && (
                      <button
                        type="button"
                        onClick={() => onRevoke(m.user_id, m.email)}
                        className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-error-text)]"
                      >
                        Revogar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  )
}

// ---------- Modules section ----------

function ModulesSection({
  workspaceId,
  onModulesChange,
}: {
  workspaceId: string
  onModulesChange?: () => void
}) {
  const [enabled, setEnabled] = useState<WorkspaceModule[] | null>(null)
  const [catalog, setCatalog] = useState<ModuleCatalogEntry[] | null>(null)
  const [error, setError] = useState('')

  const reload = useCallback(async () => {
    const [e, c] = await Promise.all([
      adminApi.listWorkspaceModules(workspaceId),
      adminApi.listModuleCatalog(),
    ])
    setEnabled(e)
    setCatalog(c)
  }, [workspaceId])

  useEffect(() => {
    // O `reload` só escreve estado DEPOIS do await, portanto não há
    // render encadeado aqui. A regra é conservadora: marca qualquer
    // chamada a uma função que contenha setState, sem distinguir o
    // que é síncrono do que não é. Verificado em cada chamada.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void reload()
  }, [reload])

  const enabledSlugs = useMemo(
    () => new Set((enabled ?? []).map((m) => m.slug)),
    [enabled],
  )

  const onToggle = async (slug: string, isEnabled: boolean) => {
    setError('')
    try {
      if (isEnabled) {
        await adminApi.disableModule(workspaceId, slug)
      } else {
        await adminApi.enableModule(workspaceId, slug)
      }
      await reload()
      // Notify the parent so MembersSection picks up the new set.
      onModulesChange?.()
    } catch (err) {
      setError(errMsg(err))
    }
  }

  return (
    <section className="rounded border border-[var(--color-border)] bg-[var(--color-panel)]">
      <header className="border-b border-[var(--color-border-subtle)] px-3 py-2 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
        Módulos disponíveis no workspace
      </header>
      <p className="border-b border-[var(--color-border-subtle)] px-3 py-2 text-[11px] text-[var(--color-text-dim)]">
        Ativa aqui os módulos que vão estar disponíveis para os
        membros deste workspace. Depois, na tabela em cima, escolhe
        membro a membro quem recebe cada módulo (coluna &ldquo;Acessos&rdquo;).
        O viewer base está sempre disponível.
      </p>
      {error && <div className="px-3 pt-2 text-xs text-red-400">{error}</div>}
      <ul className="text-xs">
        {catalog === null ? (
          <li className="p-3 text-[var(--color-text-dim)]">A carregar…</li>
        ) : catalog.length === 0 ? (
          <li className="p-3 text-[var(--color-text-dim)]">Sem módulos.</li>
        ) : (
          catalog.map((m) => {
            const on = enabledSlugs.has(m.slug)
            return (
              <li
                key={m.slug}
                className="flex items-center justify-between border-b border-[var(--color-border-subtle)] px-3 py-2"
              >
                <div>
                  <div className="font-medium">{m.name}</div>
                  <div className="text-[var(--color-text-dim)]">
                    {m.slug}
                    {!m.is_active && ' · desativado globalmente'}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={!m.is_active && !on}
                  onClick={() => onToggle(m.slug, on)}
                  className={`rounded border px-2 py-0.5 ${
                    on
                      ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                      : 'border-[var(--color-border)] text-[var(--color-text-muted)]'
                  } ${!m.is_active && !on ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  {on ? 'Ativo' : 'Ativar'}
                </button>
              </li>
            )
          })
        )}
      </ul>
    </section>
  )
}

// ---------- Audit section ----------

interface ActionPreset {
  value: string
  label: string
}

const AUDIT_ACTION_PRESETS: ActionPreset[] = [
  { value: '', label: 'Todas as ações' },
  { value: 'auth.login', label: 'Logins' },
  { value: 'auth.password.set', label: 'Definir password' },
  { value: 'auth.password.change', label: 'Alterar password' },
  { value: 'admin.user.set_password', label: 'Admin definiu password' },
  { value: 'admin.member.add', label: 'Adicionou membro' },
  { value: 'admin.member.revoke', label: 'Revogou membro' },
  { value: 'admin.member.modules', label: 'Alterou acessos' },
  { value: 'admin.module.enable', label: 'Ativou módulo' },
  { value: 'admin.module.disable', label: 'Desativou módulo' },
  { value: 'admin.workspace.archive', label: 'Arquivou workspace' },
  { value: 'bcf.import', label: 'Importou BCF' },
  { value: 'bcf.export', label: 'Exportou BCF' },
  { value: 'bcf.project.create', label: 'Criou projeto BCF' },
  { value: 'bcf.topic.create', label: 'Criou tópico' },
  { value: 'bcf.topic.update', label: 'Alterou tópico' },
  { value: 'bcf.comment.create', label: 'Comentou' },
]

function actionLabel(action: string): string {
  const preset = AUDIT_ACTION_PRESETS.find((p) => p.value === action)
  return preset?.label ?? action
}

function formatMetadata(action: string, metadata: Record<string, unknown>): string {
  if (!metadata || Object.keys(metadata).length === 0) return ''
  // Friendly summaries for the most common shapes; fall back to JSON.
  if (action === 'auth.login') {
    const method = metadata.method as string | undefined
    const forced = metadata.forced as boolean | undefined
    return [
      method ? `via ${method}` : null,
      forced ? 'forçado' : null,
    ]
      .filter(Boolean)
      .join(' · ')
  }
  if (action === 'admin.user.set_password') {
    const email = metadata.target_email as string | undefined
    return email ? `→ ${email}` : ''
  }
  if (action === 'admin.member.add') {
    const email = metadata.member_email as string | undefined
    return email ? `→ ${email}` : ''
  }
  if (action === 'admin.member.revoke') {
    const uid = metadata.revoked_user_id as string | undefined
    return uid ? `→ ${uid.slice(0, 8)}…` : ''
  }
  if (action === 'admin.member.modules') {
    const slugs = metadata.module_slugs as string[] | undefined
    const target = metadata.target_user_id as string | undefined
    const parts: string[] = []
    if (target) parts.push(`→ ${target.slice(0, 8)}…`)
    if (slugs && slugs.length) parts.push(`módulos: ${slugs.join(', ')}`)
    else if (slugs) parts.push('sem módulos')
    return parts.join(' · ')
  }
  if (action === 'admin.module.enable' || action === 'admin.module.disable') {
    const m = metadata.module as string | undefined
    return m ? `módulo: ${m}` : ''
  }
  if (action === 'bcf.import') {
    const fn = metadata.filename as string | undefined
    const inserted = metadata.topics_inserted
    return [fn, typeof inserted === 'number' ? `${inserted} tópicos` : null]
      .filter(Boolean)
      .join(' · ')
  }
  if (action === 'bcf.export') {
    const topics = metadata.topics
    const v = metadata.version
    return [
      typeof topics === 'number' ? `${topics} tópicos` : null,
      v ? `BCF ${v}` : null,
    ]
      .filter(Boolean)
      .join(' · ')
  }
  if (action === 'bcf.project.create') {
    const name = metadata.name as string | undefined
    return name ? `«${name}»` : ''
  }
  if (action === 'bcf.topic.create') {
    const number = metadata.number
    const title = metadata.title as string | undefined
    const parts: string[] = []
    if (typeof number === 'number') parts.push(`#${number}`)
    if (title) parts.push(title)
    return parts.join(' · ')
  }
  if (action === 'bcf.topic.update') {
    const flds = metadata.fields as string[] | undefined
    return flds && flds.length ? `campos: ${flds.join(', ')}` : ''
  }
  if (action === 'bcf.comment.create') {
    return ''
  }
  return JSON.stringify(metadata)
}

// ---------- Workspace detail column ----------

function WorkspaceDetail({
  workspaceId,
  onArchived,
}: {
  workspaceId: string
  onArchived: () => void
}) {
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [archiveError, setArchiveError] = useState('')
  // Counter that bumps every time the modules-enabled set changes for
  // this workspace. ModulesSection bumps it after enable/disable;
  // MembersSection re-runs its reload effect when the counter ticks
  // so the per-member grant buttons reflect the new module list
  // immediately (without forcing the admin to refresh the page).
  const [modulesVersion, setModulesVersion] = useState(0)

  useEffect(() => {
    void adminApi
      .listWorkspaces()
      .then((all) => setWorkspace(all.find((w) => w.id === workspaceId) ?? null))
  }, [workspaceId])

  const onArchive = async () => {
    if (!window.confirm('Arquivar este workspace? Os ficheiros serão apagados em 7 dias.')) return
    setArchiveError('')
    try {
      const updated = await adminApi.archiveWorkspace(workspaceId)
      setWorkspace(updated)
      onArchived()
    } catch (err) {
      setArchiveError(errMsg(err))
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">
            {workspace?.name ?? '…'}
          </h2>
          <p className="text-xs text-[var(--color-text-dim)]">
            {workspace?.slug}
            {workspace?.expires_at && ` · expira ${fmt(workspace.expires_at)}`}
            {workspace?.archived_at && ` · arquivado ${fmt(workspace.archived_at)}`}
          </p>
        </div>
        {workspace && !workspace.archived_at && (
          <button
            type="button"
            onClick={onArchive}
            className="rounded border border-red-700/40 px-3 py-1 text-xs text-[var(--color-error-text)] hover:bg-[var(--color-error-bg)]"
          >
            Arquivar workspace
          </button>
        )}
      </header>
      {archiveError && <div className="text-xs text-red-400">{archiveError}</div>}
      <MembersSection
        workspaceId={workspaceId}
        modulesVersion={modulesVersion}
      />
      <ModulesSection
        workspaceId={workspaceId}
        onModulesChange={() => setModulesVersion((v) => v + 1)}
      />
    </div>
  )
}

// ---------- Page ----------

// External operational dashboards. The platform has zero file storage
// on the cloud (per CLAUDE.md decision 2026-04-27), only Postgres on
// Neon holds workspaces, members, magic_links, sessions, audit_log, all
// tiny rows. Daily ops telemetry that we don't (and shouldn't) duplicate
// in our own UI lives in these vendor consoles. The dropdown is purely
// a bookmark hub: clicking a link opens the vendor in a new tab.
const EXTERNAL_DASHBOARDS: { label: string; href: string; hint: string }[] = [
  {
    label: 'Cloudflare',
    href: 'https://dash.cloudflare.com/',
    hint: 'Pages: deploys do frontend, analytics, DNS de openbim.vn2r.pt',
  },
  {
    label: 'Render',
    href: 'https://dashboard.render.com/',
    hint: 'Web service do FastAPI: logs, métricas, cron de cleanup, redeploys',
  },
  {
    label: 'Neon',
    href: 'https://console.neon.tech/',
    hint: 'Postgres: uso de storage, queries, branches da BD',
  },
  {
    label: 'Resend',
    href: 'https://resend.com/emails',
    hint: 'Logs de magic-link emails enviados, deliverability, bounces',
  },
  {
    label: 'UptimeRobot',
    href: 'https://uptimerobot.com/dashboard',
    hint: 'Uptime de api.openbim.vn2r.pt (ping anti-cold-start)',
  },
  {
    label: 'GitHub',
    href: 'https://github.com/RPGAVINA/vn2r-openbim-hub',
    hint: 'Repo: código, issues, PRs, Actions',
  },
]

function ExternalDashboards() {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        title="Dashboards externos (Cloudflare, Render, Neon, Resend…)"
      >
        Dashboards ↗
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default bg-transparent"
          />
          <div className="absolute left-0 top-full z-20 mt-1 w-72 rounded border border-[var(--color-border)] bg-[var(--color-panel)] p-1 shadow-xl">
            <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-[var(--color-text-dim)]">
              Telemetria operacional
            </div>
            {EXTERNAL_DASHBOARDS.map((d) => (
              <a
                key={d.href}
                href={d.href}
                target="_blank"
                rel="noreferrer noopener"
                onClick={() => setOpen(false)}
                className="block rounded px-2 py-1.5 text-xs hover:bg-[var(--color-panel-elevated)]"
              >
                <div className="font-medium text-[var(--color-text)]">
                  {d.label} <span className="text-[10px]">↗</span>
                </div>
                <div className="text-[10px] text-[var(--color-text-dim)]">
                  {d.hint}
                </div>
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

type AdminTab = 'workspaces' | 'activity' | 'dashboards'

function tabFromHash(hash: string): AdminTab {
  const h = hash.replace(/^#/, '')
  if (h === 'activity' || h === 'audit') return 'activity'
  if (h === 'dashboards') return 'dashboards'
  return 'workspaces'
}

function AdminPage() {
  const { user, workspace } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [orgId, setOrgId] = useState<string | null>(null)
  const [workspaceId, setWorkspaceId] = useState<string | null>(null)
  const [refreshNonce, setRefreshNonce] = useState(0)
  const tab = tabFromHash(location.hash)

  const setTab = (next: AdminTab) => {
    const hash = next === 'workspaces' ? '' : `#${next}`
    navigate(`${location.pathname}${hash}`, { replace: false })
  }

  if (!user || !user.is_vn2r_admin) {
    return (
      <div className="flex h-full items-center justify-center bg-[var(--color-bg)] text-sm text-[var(--color-text-muted)]">
        Acesso restrito à equipa VN2R.
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col bg-[var(--color-bg)] text-[var(--color-text)]">
      <header className="flex h-10 shrink-0 items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-panel)] px-3 text-sm">
        <span className="flex items-center gap-2">
          <span className="text-sm font-semibold tracking-wider text-[var(--color-text)]">
            VN2R
          </span>
          <span className="text-xs font-medium tracking-tight text-[var(--color-text-muted)]">
            <span className="text-[var(--color-accent)]">openBIM</span> Hub · Admin
          </span>
        </span>
        <nav className="flex items-center gap-1 text-xs">
          {(
            [
              { id: 'workspaces', label: 'Workspaces' },
              { id: 'activity', label: 'Atividade' },
              { id: 'dashboards', label: 'Dashboards' },
            ] as { id: AdminTab; label: string }[]
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`rounded px-2 py-0.5 ${
                tab === t.id
                  ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <ExternalDashboards />
        <div className="ml-auto text-xs text-[var(--color-text-dim)]">{user.email}</div>
        <Link
          to={workspace ? `/${workspace.slug}/` : '/'}
          className="rounded border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          Voltar ao Hub
        </Link>
      </header>
      {tab === 'workspaces' && (
        <div className="flex min-h-0 flex-1">
          <OrgColumn
            selected={orgId}
            onSelect={(id) => {
              setOrgId(id)
              setWorkspaceId(null)
            }}
          />
          <WorkspaceColumn
            key={`ws-${orgId}-${refreshNonce}`}
            organizationId={orgId}
            selected={workspaceId}
            onSelect={setWorkspaceId}
          />
          {workspaceId ? (
            <WorkspaceDetail
              key={`detail-${workspaceId}-${refreshNonce}`}
              workspaceId={workspaceId}
              onArchived={() => setRefreshNonce((n) => n + 1)}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center text-xs text-[var(--color-text-dim)]">
              Seleciona um workspace.
            </div>
          )}
        </div>
      )}
      {tab === 'activity' && <ActivityTab />}
      {tab === 'dashboards' && <DashboardsTab />}
    </div>
  )
}

// ---------- Activity tab ----------

function ActivityTab() {
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [orgId, setOrgId] = useState('')
  const [workspaceId, setWorkspaceId] = useState('')
  const [email, setEmail] = useState('')
  const [action, setAction] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [entries, setEntries] = useState<AuditEntry[] | null>(null)
  const [error, setError] = useState('')
  const [limit] = useState(200)

  // Load orgs once + workspaces filtered by org so the user can drill
  // down without leaving the tab.
  useEffect(() => {
    void adminApi.listOrganizations().then(setOrgs)
  }, [])
  useEffect(() => {
    void adminApi.listWorkspaces(orgId || undefined).then(setWorkspaces)
  }, [orgId])
  // Mudar de organização invalida o workspace escolhido. Ajusta-se
  // durante o render, com guarda, que é o padrão do React para isto.
  const [orgAdoptada, setOrgAdoptada] = useState(orgId)
  if (orgId !== orgAdoptada) {
    setOrgAdoptada(orgId)
    setWorkspaceId('')
  }

  const reload = useCallback(async () => {
    try {
      const res = await adminApi.listAudit({
        workspace_id: workspaceId || undefined,
        organization_id: orgId || undefined,
        email: email.trim() || undefined,
        action: action || undefined,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
        limit,
      })
      setEntries(res)
      // Limpar o erro aqui e não antes da chamada: antes, era um
      // setState síncrono no caminho do efeito.
      setError('')
    } catch (err) {
      setError(errMsg(err))
    }
  }, [orgId, workspaceId, email, action, from, to, limit])

  useEffect(() => {
    // O `reload` só escreve estado DEPOIS do await, portanto não há
    // render encadeado aqui. A regra é conservadora: marca qualquer
    // chamada a uma função que contenha setState, sem distinguir o
    // que é síncrono do que não é. Verificado em cada chamada.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void reload()
  }, [reload])

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
      <section className="rounded border border-[var(--color-border)] bg-[var(--color-panel)]">
        <header className="flex flex-wrap items-center gap-2 border-b border-[var(--color-border-subtle)] px-3 py-2 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
          <span>Filtros</span>
          <select
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-xs normal-case font-normal tracking-normal text-[var(--color-text)]"
          >
            <option value="">Todas as organizações</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          <select
            value={workspaceId}
            onChange={(e) => setWorkspaceId(e.target.value)}
            disabled={!orgId && workspaces.length === 0}
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-xs normal-case font-normal tracking-normal text-[var(--color-text)]"
          >
            <option value="">Todos os workspaces</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email contém…"
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-xs normal-case font-normal tracking-normal text-[var(--color-text)]"
          />
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-xs normal-case font-normal tracking-normal text-[var(--color-text)]"
          >
            {AUDIT_ACTION_PRESETS.map((p) => (
              <option key={p.value || 'all'} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1 text-xs normal-case font-normal tracking-normal text-[var(--color-text-dim)]">
            De
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-xs text-[var(--color-text)]"
            />
          </label>
          <label className="flex items-center gap-1 text-xs normal-case font-normal tracking-normal text-[var(--color-text-dim)]">
            Até
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-xs text-[var(--color-text)]"
            />
          </label>
          <button
            type="button"
            onClick={() => void reload()}
            className="ml-auto rounded border border-[var(--color-border)] px-2 py-0.5 text-xs normal-case font-normal tracking-normal text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            Atualizar
          </button>
        </header>
        {error && <div className="px-3 pt-2 text-xs text-red-400">{error}</div>}
        <div className="overflow-auto">
          <table className="w-full text-xs">
            <thead className="text-[var(--color-text-dim)]">
              <tr>
                <th className="border-y border-[var(--color-border-subtle)] px-3 py-1.5 text-left font-normal">
                  Quando
                </th>
                <th className="border-y border-[var(--color-border-subtle)] px-3 py-1.5 text-left font-normal">
                  Quem
                </th>
                <th className="border-y border-[var(--color-border-subtle)] px-3 py-1.5 text-left font-normal">
                  Ação
                </th>
                <th className="border-y border-[var(--color-border-subtle)] px-3 py-1.5 text-left font-normal">
                  Detalhes
                </th>
              </tr>
            </thead>
            <tbody>
              {entries === null ? (
                <tr>
                  <td colSpan={4} className="p-3 text-[var(--color-text-dim)]">
                    A carregar…
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-3 text-[var(--color-text-dim)]">
                    Sem registos para os filtros ativos.
                  </td>
                </tr>
              ) : (
                entries.map((e) => (
                  <tr
                    key={e.id}
                    className="border-b border-[var(--color-border-subtle)] hover:bg-[var(--color-panel-elevated)]"
                  >
                    <td className="px-3 py-1.5 font-mono text-[var(--color-text-muted)]">
                      {fmt(e.created_at)}
                    </td>
                    <td className="px-3 py-1.5">
                      {e.user_email ?? (
                        <span className="text-[var(--color-text-dim)]">-</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">{actionLabel(e.action)}</td>
                    <td className="px-3 py-1.5 text-[var(--color-text-muted)]">
                      {formatMetadata(e.action, e.metadata)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {entries && entries.length === limit && (
          <p className="px-3 py-2 text-[11px] text-[var(--color-text-dim)]">
            A mostrar os últimos {limit} registos. Restringe por filtros para ver mais antigos.
          </p>
        )}
      </section>
    </div>
  )
}

// ---------- Dashboards tab ----------

function DashboardsTab() {
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [orgId, setOrgId] = useState('')
  const [workspaceId, setWorkspaceId] = useState('')
  const [days, setDays] = useState(30)
  const [data, setData] = useState<AuditAggregate | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    void adminApi.listOrganizations().then(setOrgs)
  }, [])
  useEffect(() => {
    void adminApi.listWorkspaces(orgId || undefined).then(setWorkspaces)
  }, [orgId])
  // Mudar de organização invalida o workspace escolhido. Ajusta-se
  // durante o render, com guarda, que é o padrão do React para isto.
  const [orgAdoptada, setOrgAdoptada] = useState(orgId)
  if (orgId !== orgAdoptada) {
    setOrgAdoptada(orgId)
    setWorkspaceId('')
  }

  useEffect(() => {
    void adminApi
      .aggregateAudit({
        workspace_id: workspaceId || undefined,
        organization_id: orgId || undefined,
        days,
      })
      .then(setData)
      .catch((err) => setError(errMsg(err)))
  }, [orgId, workspaceId, days])

  const maxDay = useMemo(
    () => Math.max(1, ...(data?.by_day ?? []).map((b) => b.count)),
    [data],
  )
  const maxAction = useMemo(
    () => Math.max(1, ...(data?.by_action ?? []).map((b) => b.count)),
    [data],
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
      <section className="rounded border border-[var(--color-border)] bg-[var(--color-panel)] p-3">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
            Janela
          </span>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-xs text-[var(--color-text)]"
          >
            <option value={7}>Últimos 7 dias</option>
            <option value={30}>Últimos 30 dias</option>
            <option value={90}>Últimos 90 dias</option>
            <option value={180}>Últimos 180 dias</option>
            <option value={365}>Últimos 365 dias</option>
          </select>
          <select
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-xs text-[var(--color-text)]"
          >
            <option value="">Todas as organizações</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          <select
            value={workspaceId}
            onChange={(e) => setWorkspaceId(e.target.value)}
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-xs text-[var(--color-text)]"
          >
            <option value="">Todos os workspaces</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
          <span className="ml-auto text-xs text-[var(--color-text-dim)]">
            Total na janela:{' '}
            <span className="font-medium text-[var(--color-text)]">
              {data?.total ?? '…'}
            </span>
          </span>
        </div>
      </section>

      {error && (
        <div className="rounded border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-3 py-2 text-xs text-[var(--color-error-text)]">
          {error}
        </div>
      )}

      <section className="rounded border border-[var(--color-border)] bg-[var(--color-panel)] p-3">
        <header className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
          Eventos por dia
        </header>
        {data === null ? (
          <div className="text-xs text-[var(--color-text-dim)]">A carregar…</div>
        ) : data.by_day.length === 0 ? (
          <div className="text-xs text-[var(--color-text-dim)]">
            Sem atividade na janela.
          </div>
        ) : (
          <div className="flex items-end gap-1 overflow-x-auto" style={{ height: 120 }}>
            {data.by_day.map((b) => (
              <div
                key={b.label}
                className="flex flex-col items-center"
                style={{ minWidth: 16 }}
                title={`${b.label}: ${b.count}`}
              >
                <div
                  className="w-3 rounded-t bg-[var(--color-accent)]"
                  style={{
                    height: `${Math.max(2, (b.count / maxDay) * 100)}%`,
                  }}
                />
                <span className="mt-1 -rotate-90 origin-top-left text-[8px] text-[var(--color-text-dim)] whitespace-nowrap">
                  {b.label.slice(5)}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded border border-[var(--color-border)] bg-[var(--color-panel)] p-3">
        <header className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
          Eventos por ação
        </header>
        {data === null ? (
          <div className="text-xs text-[var(--color-text-dim)]">A carregar…</div>
        ) : data.by_action.length === 0 ? (
          <div className="text-xs text-[var(--color-text-dim)]">
            Sem atividade na janela.
          </div>
        ) : (
          <ul className="flex flex-col gap-1">
            {data.by_action.map((b) => (
              <li
                key={b.label}
                className="flex items-center gap-2 text-xs"
                title={`${b.label}: ${b.count}`}
              >
                <span className="w-48 truncate text-[var(--color-text-muted)]">
                  {actionLabel(b.label)}
                </span>
                <div className="flex-1 overflow-hidden rounded bg-[var(--color-bg)]">
                  <div
                    className="h-2 bg-[var(--color-accent)]"
                    style={{ width: `${(b.count / maxAction) * 100}%` }}
                  />
                </div>
                <span className="w-10 text-right font-mono text-[var(--color-text)]">
                  {b.count}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded border border-[var(--color-border)] bg-[var(--color-panel)] p-3">
        <header className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
          Top utilizadores (top 20)
        </header>
        {data === null ? (
          <div className="text-xs text-[var(--color-text-dim)]">A carregar…</div>
        ) : data.by_user.length === 0 ? (
          <div className="text-xs text-[var(--color-text-dim)]">
            Sem atividade na janela.
          </div>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {data.by_user.map((b) => (
              <li
                key={b.label}
                className="flex items-center justify-between rounded px-2 py-1 text-xs hover:bg-[var(--color-panel-elevated)]"
              >
                <span className="truncate text-[var(--color-text)]">
                  {b.label}
                </span>
                <span className="font-mono text-[var(--color-text-muted)]">
                  {b.count}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

export default AdminPage
